import mapValues from 'lodash/mapValues';

import type { UtilityContext } from '@change-corgi/core/react/utilityContext';

import type { FcmValues, Petition, PromotionStats, PsfPromoteOrSharePageData, Recruiter, Viewer } from './types';

const CHIP_IN_USD_FALLBACK = 3;

type PsfComboBanditData = {
	amount: number;
	currency: string;
	amountLocalized: string;
	amountId: string;
};

export function normalizeData(
	utilityContext: UtilityContext,
	{
		petition,
		promotionStats,
		viewer,
		recruiter,
		fcmValues,
	}: {
		petition: Petition;
		promotionStats: PromotionStats;
		viewer: Viewer;
		recruiter?: Recruiter;
		fcmValues: FcmValues;
	},
): PsfPromoteOrSharePageData {
	const {
		bandits: { id, ...petitionBandits },
		...partialPetition
	} = petition;

	const bandits = mapValues(petitionBandits, ({ variants, ...variant }) => ({
		...variant,
		...(variants?.overrideVariant ? { variant: variants.overrideVariant, overridden: true } : { overridden: false }),
	}));

	return {
		petition: { ...partialPetition, promotionStats },
		recruiter,
		bandits,
		joinTheCommunityCustomConfig: normalizeJoinTheCommunityCustomConfig(utilityContext, {
			viewer,
			fcmValues,
		}),
		...normalizeAmount(utilityContext, bandits, fcmValues, viewer),
		...normalizePromotionGoal(utilityContext, promotionStats.promoterCount),
	};
}

function normalizeAmount(
	utilityContext: UtilityContext,
	bandits: PsfPromoteOrSharePageData['bandits'],
	fcmValues: FcmValues,
	viewer: Viewer,
) {
	const { i18n } = utilityContext;
	const viewerCurrency = viewer.country.currency || { code: 'USD', exchangeRate: 1 };
	if (bandits.psfCombo.variant) {
		const banditAmount = // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			bandits.psfCombo.variant.data as unknown as PsfComboBanditData;
		if (banditAmount) return { chipInAmount: banditAmount.amount, chipInAmountLocalized: banditAmount.amountLocalized };
	}
	const chipInAmount = fcmValues.chipInAmount || CHIP_IN_USD_FALLBACK * viewerCurrency.exchangeRate;
	return { chipInAmount, chipInAmountLocalized: i18n.localizeCurrency(chipInAmount, viewerCurrency?.code) };
}

function normalizePromotionGoal(utilityContext: UtilityContext, promoterCount: number) {
	const { i18n } = utilityContext;
	let divideCounter = 0;
	let tempPromoterCount = promoterCount;
	while (tempPromoterCount >= 10) {
		tempPromoterCount /= 10;
		divideCounter += 1;
	}
	const calculatedGoal = (Math.floor(tempPromoterCount) + 1) * 10 ** divideCounter;

	return {
		promotionGoal: calculatedGoal,
		promotionGoalLocalized: i18n.localizeNumber(calculatedGoal),
	};
}

function normalizeJoinTheCommunityCustomConfig(
	{ i18n }: UtilityContext,
	{
		viewer,
		fcmValues,
	}: {
		viewer: Viewer;
		fcmValues: FcmValues;
	},
) {
	if (!fcmValues.joinCommunityFooterConfig) return undefined;

	const viewerCurrency = viewer.country.currency || { code: 'USD', exchangeRate: 1 };

	const {
		custom_promoter_count: promoterCount,
		custom_contribution_amount: contributionAmount,
		custom_additional_signatures: additionalSignatures,
	} = fcmValues.joinCommunityFooterConfig;
	return {
		promoterCount,
		promoterCountLocalized: i18n.localizeNumber(promoterCount),
		contributionAmount,
		contributionAmountLocalized: i18n.localizeCurrency(contributionAmount, viewerCurrency.code),
		additionalSignatures,
		additionalSignaturesLocalized: i18n.localizeNumber(additionalSignatures),
	};
}
